import type { AppRouter } from '@app/api'
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server'
import { createTRPCContext } from '@trpc/tanstack-react-query'

/**
 * Typed tRPC hooks. `useTRPC()` returns a proxy whose leaves expose
 * `.queryOptions(...)` / `.mutationOptions(...)` for TanStack Query — the same
 * ergonomics. The router type flows straight from the API package,
 * so client and server can never drift.
 */
export const { TRPCProvider, useTRPC, useTRPCClient } =
  createTRPCContext<AppRouter>()

/**
 * What a procedure answers with, for a component that takes it as a prop.
 *
 * Derived from the router rather than imported from the module that defines it:
 * the API package exports its router and nothing else, which is what keeps a
 * screen from reaching into a service's internals to name a type. A procedure
 * that changes shape breaks the component at compile time either way — this
 * just keeps the door where the architecture put it.
 */
export type RouterOutputs = inferRouterOutputs<AppRouter>

/**
 * What a procedure takes, for a component that hands one back to its screen.
 *
 * The same door as the outputs, and the case for it is a partial write: a form
 * that sends only what moved has to name the shape of "only what moved", and
 * naming it a second time by hand is how the two come apart.
 */
export type RouterInputs = inferRouterInputs<AppRouter>
